import {Button, Card, Col, Modal, Row} from "antd";
import React, {useEffect, useState} from "react";
import styles from './Training.module.scss'
import {useAppDispatch, useAppSelector} from "../../hooks";
import {fetchTrainingItems, selectTrainingItems} from "./trainingSlice";
import {TrainingItem, TrainingItemType} from "../../actions/api";
import {
    CloseOutlined,
    FileFilled,
    FilePdfFilled,
    FilePptFilled,
    FileWordFilled, LoadingOutlined,
    YoutubeFilled
} from "@ant-design/icons";

const ItemIcon = ({trainingItem}: {trainingItem: TrainingItem}) => {
    if (trainingItem.type === TrainingItemType.VIDEO) {
        return <YoutubeFilled/>
    }

    if (trainingItem.url.endsWith('.pdf')) {
        return <FilePdfFilled/>
    }

    if (trainingItem.url.endsWith('.ppt')) {
        return <FilePptFilled/>
    }

    if (trainingItem.url.endsWith('.doc') || trainingItem.url.endsWith('docx')) {
        return <FileWordFilled/>
    }

    return <FileFilled/>
}

export const toEmbedUrl = (urlString: string) => {
    const url = new URL(urlString);

    if (url.pathname.startsWith('/embed/')) {
        const videoId = url.pathname.split('/embed/')[1];
        return `${url.origin}/embed/${videoId}?modestbranding=1&rel=0`;
    }
    const videoId = url.searchParams.get('v');
    if (videoId) {
        return `${url.origin}/embed/${videoId}?modestbranding=1&rel=0`;
    }
    return urlString;
};

export const VideoModal = ({ trainingItem, open, onCancel }: {
    trainingItem: TrainingItem;
    open: boolean;
    onCancel: () => void;
}) => {
    const width = 1124;

   const getEmbedUrl = (url: string) => {
        try {
            const urlObj = new URL(url);

            if (urlObj.hostname.includes('youtube') || urlObj.hostname.includes('youtu.be')) {
                let videoId = '';
                if (urlObj.hostname === 'youtu.be') {
                    videoId = urlObj.pathname.slice(1); // Extract after '/'
                } else {
                    videoId = urlObj.searchParams.get('v') || '';
                }
                if (videoId) {
                    return `https://www.youtube.com/embed/${videoId}`;
                }
            } else if (urlObj.hostname.includes('vimeo')) {
                const videoId = urlObj.pathname.split('/').filter(Boolean).pop();
                if (videoId && /^\d+$/.test(videoId)) { // Ensure it's numeric
                    return `https://player.vimeo.com/video/${videoId}`;
                }
            }

            return url; // Return the original URL if no valid ID is found
        } catch (error) {
            return url; // Return original URL in case of parsing errors
        }
    };


    return (
        <Modal
            className={styles.videoModal}
            destroyOnClose
            open={open}
            title={null}
            onCancel={onCancel}
            width={width + 48}
            closable={false}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <h3 data-testid='training-item-video-title'>{trainingItem.title}</h3>
                    <Button className={styles.closeButton} type="primary" onClick={onCancel} shape="circle" icon={<CloseOutlined />} />
                </div>
            }
        >
            <iframe
                width={width}
                height="632"
                src={getEmbedUrl(trainingItem.url)}
                title="Video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </Modal>
    );
};

export const TrainingCard = ({trainingItem, setIsModalVisible}: {
    trainingItem: TrainingItem,
    setIsModalVisible: (value: boolean) => void
}) => {
    return <Card
        data-testid='training-item-card'
        onClick={() => {
            if (trainingItem.type === TrainingItemType.VIDEO) {
                setIsModalVisible(true)
            }
        }}
        className={styles.trainingCard}
        hoverable
        cover={<img alt='training item thumbnail' src={trainingItem.thumb}/>}
    >
        <div className={styles.trainingCardIcon}>
            <ItemIcon trainingItem={trainingItem}/>
        </div>
        <div className={styles.trainingCardCategory}>
            <div>{trainingItem.category}</div>
        </div>
        <Card.Meta
            title={<div data-testid={'training-item-title'}>{trainingItem.title}</div>}
            description={trainingItem.description}
            className={styles.trainingCardMeta}
        />
    </Card>;
}

export const TrainingCardCol = ({trainingItem}: {trainingItem: TrainingItem}) => {
    const [isModalVisible, setIsModalVisible] = useState(false);

    return <Col flex="0 0 310px">
        {
            trainingItem.type === TrainingItemType.VIDEO ?
                <TrainingCard trainingItem={trainingItem} setIsModalVisible={setIsModalVisible}/>
                : <a target='_blank' rel="noopener noreferrer" href={trainingItem.url}>
                    <TrainingCard trainingItem={trainingItem} setIsModalVisible={setIsModalVisible}/>
                </a>
        }
        {trainingItem.type === TrainingItemType.VIDEO ?
            <VideoModal
                trainingItem={trainingItem}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
            />
            : null
        }
    </Col>
}

export const Training = () => {
    const trainingItems = useAppSelector(selectTrainingItems)
    const dispatcher = useAppDispatch();

    useEffect(() => {
        dispatcher(fetchTrainingItems())
    }, [dispatcher])

    return <div className={styles.training}>
        {
            trainingItems === undefined ?
            <LoadingOutlined/>
            : <Row gutter={[{xs: 8, sm: 16, md: 16, lg: 16}, {xs: 8, sm: 16, md: 24, lg: 32}]}>
                {(trainingItems ?? []).map(trainingItem => <TrainingCardCol key={trainingItem.id} trainingItem={trainingItem}/>)}
            </Row>
        }
    </div>;
}
